export type UiLocale = "en" | "zh" | "ja" | "ko";

const LOCALE_KEY = "ead-expui.locale.v1";
const LOCALES: UiLocale[] = ["en", "zh", "ja", "ko"];
const localeBus = new EventTarget();

type Dict = Record<string, string>;

const EN: Dict = {
  "brand.name": "EAD Explore",
  "lang.select": "Select language",
  "lang.current": "Current language",
  "lang.en": "English",
  "lang.zh": "Chinese",
  "lang.ja": "Japanese",
  "lang.ko": "Korean",

  "tab.eadDiagramStandard": "EAD Diagram Standard",
  "tab.eadSkills": "EAD Skills",
  "tab.eadWorkflowRules": "EAD Workflow Rules",
  "tab.exploreTypes": "Explore Types",
  "tab.projects": "Project Templates",
  "tab.runs": "Auto Test Run",
  "tab.closedRuns": "Completed Project Run",
  "tab.skills": "Skills",
  "tab.chat": "Chat",
  "tab.chatMinimal": "Test Chat",
  "tab.generateChat": "Project Chat",

  "nav.explorePlanning": "Explore Planning",
  "nav.activeProjectRun": "Active Project Run",
  "nav.projectInteractive": "Project Interactive",
  "nav.learningExplore": "Learning EAD Explore",
  "nav.completedProjectRun": "Completed Project Run",
  "nav.searchRuns": "Search runs",
  "nav.searchTemplateRun": "Search template/run",
  "nav.noRunsYet": "No runs yet",
  "nav.noActiveRunLatest": "No active run · Latest: {title}",
  "nav.interactiveRun": "Interactive: {title} · {meta}",
  "nav.gateway": "Gateway",
  "nav.online": "Online",
  "nav.offline": "Offline",
  "nav.legend.phase": "Phase: Initializing... / Running... / Reporting... / Done",
  "nav.legend.final": "Final: AI Finished / AI Canceled / User Finish - Done / User Finish - Canceled",
  "gateway.unreachable.temporary": "Gateway is temporarily unreachable. Auto-retrying in the background.",
  "app.closeNavigation": "Close navigation",
  "app.openMenu": "Open menu",
  "app.closeMenu": "Close menu",
  "app.selectSection": "Select a section from the sidebar.",
  "gateway.notConnected.title": "Hermes backend not connected",
  "gateway.notConnected.help":
    "Activate your EAD-Agent venv so hermes is on your PATH, then run hermes gateway run -vv (port 8642). The UI rechecks every few seconds; refresh if it stays offline.",

  "projects.loading": "Loading templates...",
  "projects.templates": "Templates ({count})",
  "projects.new": "+ New",
  "projects.refreshing": "Refreshing list...",
  "projects.noUrl": "No URL",
  "projects.selectTemplate": "Select a template from the list",
  "projects.canonical": "canonical",
  "projects.nodes": "nodes",
  "projects.createTitle": "New Explore Plan Project",
  "projects.createSubtitle": "Create a focused project definition before starting a run.",
  "projects.cancel": "Cancel",
  "projects.create": "Create",
  "projects.creating": "Creating...",
  "projects.chooseLearningModeTitle": "Choose Learning Mode",
  "projects.chooseLearningModeSubtitle":
    "Select the primary goal for this run. This controls AI behavior and delivery focus.",
  "projects.learningModePfmTitle": "Product feature map explore",
  "projects.learningModePfmDesc":
    "Build or improve the PFM node tree (structure, hierarchy, node quality).",
  "projects.learningModeDeepTitle": "Deep learning (features & test cases)",
  "projects.learningModeDeepDesc":
    "Deepen selected node reports while keeping the tree mostly stable.",
  "projects.learningModeContinue": "Continue",
  "projects.learningModeBack": "Back",
  "projects.learningModeRequiredFirst":
    "No baseline PFM tree exists for this template yet. Complete at least one feature-map explore run first.",
  "projects.startRunTitle": "Start Explore Run",
  "projects.startRunSubtitle": "Confirm run settings before execution starts.",
  "projects.startRun": "Start Run",
  "projects.starting": "Starting...",
  "projects.runMenuTitle": "Run menu",
  "projects.editTitle": "Edit Explore Plan Project",
  "projects.editSubtitle": "Update project definition and save changes.",
  "projects.save": "Save",
  "projects.saving": "Saving...",
  "projects.deleteConfirm": "Delete explore plan?",
  "projects.deleteTemplate": "Delete template",
  "projects.notNow": "Not now",
  "projects.field.name": "Project Name",
  "projects.field.exploreType": "Explore Type",
  "projects.field.targetUrl": "Target URL",
  "projects.field.duration": "Explore Time Duration (minutes)",
  "projects.field.description": "Description",
  "projects.field.aiPrompt": "AI Prompt",
  "projects.field.runTitle": "Title",
  "projects.field.runPurpose": "Run purpose",
  "projects.field.evidenceFocus": "Evidence focus",
  "projects.field.runningDuration": "Running Time Duration",
  "projects.field.aiAnswerLanguage": "AI Answer Language",
  "projects.answerLanguage.en": "English",
  "projects.answerLanguage.zh": "Chinese (Simplified)",
  "projects.answerLanguage.zh-tw": "Chinese (Traditional)",
  "projects.answerLanguage.ja": "Japanese",
  "projects.answerLanguage.ko": "Korean",
  "projects.answerLanguage.es": "Spanish",
  "projects.answerLanguage.fr": "French",
  "projects.answerLanguage.de": "German",
  "projects.answerLanguage.pt": "Portuguese",
  "projects.answerLanguage.it": "Italian",
  "projects.answerLanguage.ru": "Russian",
  "projects.answerLanguagePickerTitle": "Select AI Answer Language",
  "projects.answerLanguagePickerApply": "Apply",
  "projects.answerLanguagePickerBrowse": "Choose language",
  "projects.field.timeBudget": "Time Budget (minutes)",
  "projects.templateNotFound": "Template not found.",
  "projects.loadingPrompt": "Loading AI prompt in the background...",
  "projects.deleteBody": "This removes the project template and its linked run records from Hermes.",
  "projects.deleteWarning": "This cannot be undone. Active runs tied to this template may lose their plan link.",
  "projects.startRunBtn": "Run",
  "projects.editBtn": "Edit",
  "projects.deleteBtn": "Delete",
  "projects.field.authMode": "Auth Mode",
  "projects.field.timeBudgetSimple": "Time Budget",
  "projects.field.pfmNodes": "PFM Nodes",
  "projects.field.created": "Created",
  "projects.loadingPromptShort": "Loading prompt...",
  "projects.saveFailedTitle": "Save failed",
  "projects.saveMissingId": "Unable to save: template id is missing.",
  "projects.saveFailedMessage": "Save failed: {error}",
  "projects.deleteConnectionError": "Could not delete template. Check gateway connection.",
  "projects.deletedTitle": "Template deleted",
  "projects.deletedMessage": "\"{name}\" and its linked run records were removed.",
  "projects.promptNotLoadedTitle": "Prompt not loaded",
  "projects.promptNotLoadedMessage": "Gateway did not return template prompts. You can still Start Run — the default explore prompt will be used.",
  "projects.nameRequiredTitle": "Project name required",
  "projects.nameRequiredMessage": "Project name is required.",
  "projects.createFailedTitle": "Create failed",
  "projects.createFailedMessage": "Failed to create project: {error}",
  "projects.runActiveTitle": "Run already active",
  "projects.runActiveMessage": "Only one active run is allowed. Please finish or cancel the current active run before starting a new one.",
  "projects.runStartFailedTitle": "Could not start run",
  "projects.runIdMissingTitle": "Run ID missing",
  "projects.runIdMissingMessage": "Run may have started, but gateway did not return a run id. Open Runs and refresh. Check gateway logs for POST /v1/projects/executions/run.",

  "runs.tabs.explore": "Explore",
  "runs.tabs.activity": "Activity",
  "runs.refresh": "Refresh",
  "runs.menu": "Run menu",
  "runs.extend15": "Extend +15 min",
  "runs.extend30": "Extend +30 min",
  "runs.extend60": "Extend +1 hour",
  "runs.extending": "Extending…",
  "runs.extendingTitle": "Extending run time budget",
  "runs.stopping": "Stopping…",
  "runs.stoppingTitle": "Stopping run",
  "runs.stopDone": "Stop - Done",
  "runs.stopCancel": "Stop - Canceled",
  "runs.deleteRun": "Delete run",
  "runs.markInvalid": "Mark invalid",
  "runs.invalidAlready": "Invalid (already)",
  "runs.overview": "Run overview",
  "runs.runNumber": "Run number",
  "runs.loginPhase": "Login phase",
  "runs.runPhase": "Run phase",
  "runs.started": "Started",
  "runs.timeBudget": "Time Budget",
  "runs.duration": "Duration",
  "runs.target": "Target",
  "runs.mission": "Mission",
  "runs.aiAnswerLanguage": "AI Answer Language",
  "runs.aiAnswerLanguageUpdatedTitle": "AI answer language updated",
  "runs.aiAnswerLanguageUpdatedMessage": "Agent answer language switched to {language}.",
  "runs.browser": "Browser",
  "runs.headed": "Headed (visible)",
  "runs.headless": "Headless",
  "runs.recentActivity": "Recent Activity Progress",
  "runs.viewMore": "View more ({count})",
  "runs.noActivityYet": "No activity yet",
  "runs.activityHint":
    "During a live run the agent should post activity about every 2 minutes (reminder if none): a short readable note about what was found and what happens next, plus a screenshot when relevant. Entries appear here as they are saved to this run.",
  "runs.confirm.finishTitle": "Finish this run?",
  "runs.confirm.cancelTitle": "Cancel this run?",
  "runs.confirm.finishBody": "Exploration and the live browser session will stop. The run will be marked complete.",
  "runs.confirm.cancelBody": "The run will stop without a successful completion. Use this when the session should not count as done.",
  "runs.confirm.finishNote": "You can keep chatting on the right to polish reports, request data delivery, and work with the agent on PFM artifacts — just like during the run.",
  "runs.confirm.error.stop": "Could not stop run: {error}",
  "runs.confirm.notNow": "Not now",
  "runs.confirm.yesFinish": "Yes, finish run",
  "runs.confirm.yesCancel": "Yes, cancel run",
  "runs.confirm.deleteTitle": "Delete Run?",
  "runs.confirm.invalidateTitle": "Mark Run Invalid?",
  "runs.confirm.deleteBody": "This removes the run instance and its run artifacts. Any template training data produced by this run will also be removed.",
  "runs.confirm.invalidateBody": "This keeps the run visible for audit, but marks it Not valid for data reporting and training. It will be removed from template reports, training data, and future inherited context.",
  "runs.confirm.warning.lastRun": "This is the last run for this template. All template training data and report artifacts for this template will be deleted.",
  "runs.confirm.warning.onlyValid": "This is the only valid training run for this template. Template training data will be cleared until another run is marked valid or completed.",
  "runs.confirm.warning.currentSource": "This run is the current training source. The template will repoint to the latest remaining valid run.",
  "runs.confirm.warning.keepLatest": "The template will keep using the latest remaining valid run for training data.",
  "runs.confirm.error.actionFailed": "Action failed: {error}",
  "runs.status": "Status",
  "runs.moreActions": "More run actions",
  "runs.deleteOrInvalid": "Delete or mark invalid",
  "runs.invalidBanner": "Not valid for data reporting and training.",
  "runs.error.stopRun": "Could not stop run: {error}",
  "runs.pausedBanner": "Execution is paused; resume from the executor or gateway before the agent continues. Chat compose is locked until resumed.",
  "runs.budgetExceeded": "Time budget was exceeded for this run.",
  "runs.runningPhaseBanner":
    "Running phase: agent learns on the live app, logs activity about every 2 minutes, and delivers every 5–10 minutes. Each delivery must include a .pfm mind map and .FMR node forms. Node density: target ≤12 children per parent level; hard max 15 per parent (whole-tree cap may follow later).",
  "runs.runningPhaseInitPending":
    "Running phase starting — loading inherited PFM mind map and node reports from the prior valid run.",
  "runs.deliveryValidation.rejectedTitle": "PFM delivery rejected",
  "runs.deliveryValidation.noChangesTitle": "No delivery change",
  "runs.deliveryValidation.pendingTitle": "Delivery pending import",
  "runs.deliveryValidation.warningTitle": "Delivery saved with warnings",
  "runs.deliveryValidation.acceptedTitle": "PFM delivery accepted",
  "runs.transcriptHint": "Transcript hint: ~{tokens} tokens",
  "runs.stopNote": "Stop note: {note}",
  "runs.activitySummary": "Run activity · {entries} {entryWord}{screenshots}",
  "runs.entry": "entry",
  "runs.entries": "entries",
  "runs.screenshot": "screenshot",
  "runs.screenshots": "screenshots",
  "runs.activityTargetHint": "Target: a new log about every 2 minutes with a concise readable summary and a screenshot when relevant. If nothing arrives the executor reminds the agent. Images are saved under this run (/v1/.../reports/).",
  "runs.activity.expand": "Click to enlarge",
  "runs.activity.collapse": "Click to collapse",
  "runs.activity.viewFullscreen": "View full screen",
  "runs.activity.fullscreenAria": "Full screen activity screenshot",
  "runs.activity.closeFullscreen": "Close full screen view",
  "runs.activity.escapeHint": "Press Escape to return to the activity log.",
  "learning.title": "Continuous interactive learning",
  "learning.copy": "Inherit what this run learned for future knowledge learning.",
  "learning.validLabel":
    "This run and its PFM and EAD reports are valid for future learning.",
  "learning.hintActive": "Run still active — enable after the run finishes.",
  "learning.hintInactive": "Turn off if not valuable for future inherited learning.",
  "learning.optimize": "Optimize PFM Mindmap",
  "learning.optimizing": "Optimizing PFM mindmap…",
  "learning.optimizeQueued": "Optimization queued — agent is working on merges and reports.",
  "learning.optimizeDone": "PFM mindmap updated (new revision saved).",
  "learning.optimizeTimeout": "Optimization is taking longer than expected. Check Activity or try again.",
  "learning.optimizeFailed": "Could not start optimization.",
  "learning.createSkills": "Create skill files",
  "learning.recreateSkills": "Recreate skill files",
  "learning.confirmInvalidate": "Mark invalid for future learning?",
  "learning.confirmInvalidateCopy":
    "This run and its reports will be excluded from future inherited learning. The run stays visible for review.",
  "learning.confirmRecreate": "Recreate skill files?",
  "learning.confirmRecreateCopy":
    "This rebuilds skill files from the latest committed PFM tree and reports. Existing skill files will be replaced.",
  "learning.saving": "Saving…",
  "learning.markInvalid": "Mark invalid",
  "learning.recreate": "Recreate",
  "learning.cancel": "Cancel",
  "learning.finishRunToEnable": "Finish the run before marking learning valid.",
  "learning.noOwnMapYet": "This run does not have its EAD feature map generated yet.",
  "learning.noOwnMapYetHint":
    "Complete a PFM delivery on this run first (write .pfm → import_pfm_delivery).",

  "runs.pfm.maximize": "Maximize feature map and node report",
  "runs.pfm.exitMaximize": "Exit maximized view",
  "runs.pfm.maximizeAria": "Maximize feature map and node report panel",

  "nodeReport.refresh": "Refresh",
  "nodeReport.regenerate": "Regenerate",
  "nodeReport.translate": "Translate",
  "nodeReport.translateDisabledEnglish": "Report is already shown in English.",
  "nodeReport.translating": "Translating report for display...",
  "nodeReport.translatedViewNote": "Showing translated view ({language}). This is display-only and not saved.",
  "nodeReport.generateFull": "Generate full report",
  "nodeReport.refreshPfm": "Refresh PFM Node Report",
  "nodeReport.generatePfm": "Generate PFM Node Report",
  "nodeReport.pleaseWait": "Please wait",
  "nodeReport.refreshMenu": "Refresh menu",
  "nodeReport.longFormatRequired": "This node report is not in the mandatory long format (Features, Test Cases, Procedure Steps). Please use Regenerate.",
  "nodeReport.incompleteExplorationNote":
    "This node has a title and description only. More exploration is needed for a complete EAD report (features, test cases, and test steps). Click Refresh after further exploration.",
  "nodeReport.incompleteExplorationSaved":
    "Saved with exploration-needed note. Continue exploring this node, then Refresh to add features, test cases, and steps.",
};

const ZH: Dict = {
  "brand.name": "EAD 探索",
  "lang.select": "选择语言",
  "lang.current": "当前语言",
  "lang.en": "English",
  "lang.zh": "中文",
  "lang.ja": "日本語",
  "lang.ko": "한국어",

  "tab.eadDiagramStandard": "EAD 图表标准",
  "tab.eadSkills": "EAD 技能",
  "tab.eadWorkflowRules": "EAD 工作流规则",
  "tab.exploreTypes": "探索类型",
  "tab.projects": "项目模板",
  "tab.runs": "自动测试运行",
  "tab.closedRuns": "已完成运行",
  "tab.skills": "技能",
  "tab.chat": "聊天",
  "tab.chatMinimal": "测试聊天",
  "tab.generateChat": "项目聊天",

  "nav.explorePlanning": "探索规划",
  "nav.activeProjectRun": "进行中的项目运行",
  "nav.projectInteractive": "项目交互",
  "nav.learningExplore": "学习型 EAD 探索",
  "nav.completedProjectRun": "已完成项目运行",
  "nav.searchRuns": "搜索运行",
  "nav.searchTemplateRun": "搜索模板/运行",
  "nav.noRunsYet": "暂无运行",
  "nav.noActiveRunLatest": "暂无活动运行 · 最新: {title}",
  "nav.interactiveRun": "交互: {title} · {meta}",
  "nav.gateway": "网关",
  "nav.online": "在线",
  "nav.offline": "离线",
  "nav.legend.phase": "阶段: 初始化中... / 运行中... / 报告中... / 完成",
  "nav.legend.final": "结束: AI 完成 / AI 取消 / 用户结束-完成 / 用户结束-取消",
  "gateway.unreachable.temporary": "网关暂时不可达，正在后台自动重试。",
  "app.closeNavigation": "关闭导航",
  "app.openMenu": "打开菜单",
  "app.closeMenu": "关闭菜单",
  "app.selectSection": "请从侧边栏选择一个模块。",
  "gateway.notConnected.title": "Hermes 后端未连接",
  "gateway.notConnected.help":
    "请先激活 EAD-Agent 的 venv，确保 PATH 中有 hermes，然后执行 hermes gateway run -vv（端口 8642）。界面会每几秒重试；若仍离线请刷新页面。",

  "projects.loading": "正在加载模板...",
  "projects.templates": "模板 ({count})",
  "projects.new": "+ 新建",
  "projects.refreshing": "正在刷新列表...",
  "projects.noUrl": "无 URL",
  "projects.selectTemplate": "请从列表中选择一个模板",
  "projects.canonical": "规范版本",
  "projects.nodes": "节点",
  "projects.createTitle": "新建探索计划项目",
  "projects.createSubtitle": "先创建项目定义，再开始运行。",
  "projects.cancel": "取消",
  "projects.create": "创建",
  "projects.creating": "创建中...",
  "projects.chooseLearningModeTitle": "选择学习模式",
  "projects.chooseLearningModeSubtitle": "选择本次运行的主要目标，将影响 AI 行为与交付重点。",
  "projects.learningModePfmTitle": "产品功能图探索",
  "projects.learningModePfmDesc": "构建或改进 PFM 节点树（结构、层级、节点质量）。",
  "projects.learningModeDeepTitle": "深度学习（功能与测试用例）",
  "projects.learningModeDeepDesc": "在保持功能图基本稳定的前提下深化节点报告。",
  "projects.learningModeContinue": "继续",
  "projects.learningModeBack": "返回",
  "projects.learningModeRequiredFirst": "该模板尚无基线 PFM 树，请先完成至少一次功能图探索运行。",
  "projects.startRunTitle": "开始探索运行",
  "projects.startRunSubtitle": "运行前请确认配置。",
  "projects.startRun": "开始运行",
  "projects.starting": "启动中...",
  "projects.runMenuTitle": "运行菜单",
  "projects.editTitle": "编辑探索计划项目",
  "projects.editSubtitle": "更新项目定义并保存。",
  "projects.save": "保存",
  "projects.saving": "保存中...",
  "projects.deleteConfirm": "删除探索计划？",
  "projects.deleteTemplate": "删除模板",
  "projects.notNow": "暂不",
  "projects.field.name": "项目名称",
  "projects.field.exploreType": "探索类型",
  "projects.field.targetUrl": "目标 URL",
  "projects.field.duration": "探索时长（分钟）",
  "projects.field.description": "描述",
  "projects.field.aiPrompt": "AI 提示词",
  "projects.field.runTitle": "标题",
  "projects.field.runPurpose": "运行目的",
  "projects.field.evidenceFocus": "证据重点",
  "projects.field.runningDuration": "运行时长",
  "projects.field.aiAnswerLanguage": "AI 回答语言",
  "projects.answerLanguage.en": "English",
  "projects.answerLanguage.zh": "中文（简体）",
  "projects.answerLanguage.zh-tw": "中文（繁體）",
  "projects.answerLanguage.ja": "日本語",
  "projects.answerLanguage.ko": "한국어",
  "projects.answerLanguage.es": "Español",
  "projects.answerLanguage.fr": "Français",
  "projects.answerLanguage.de": "Deutsch",
  "projects.answerLanguage.pt": "Português",
  "projects.answerLanguage.it": "Italiano",
  "projects.answerLanguage.ru": "Русский",
  "projects.answerLanguagePickerTitle": "选择 AI 回答语言",
  "projects.answerLanguagePickerApply": "应用",
  "projects.answerLanguagePickerBrowse": "选择语言",
  "projects.field.timeBudget": "时间预算（分钟）",
  "projects.templateNotFound": "未找到模板。",
  "projects.loadingPrompt": "正在后台加载 AI 提示词...",
  "projects.deleteBody": "这将删除项目模板及其关联的运行记录。",
  "projects.deleteWarning": "此操作不可撤销。与该模板关联的活动运行可能会失去计划关联。",
  "projects.startRunBtn": "运行",
  "projects.editBtn": "编辑",
  "projects.deleteBtn": "删除",
  "projects.field.authMode": "认证模式",
  "projects.field.timeBudgetSimple": "时间预算",
  "projects.field.pfmNodes": "PFM 节点",
  "projects.field.created": "创建时间",
  "projects.loadingPromptShort": "正在加载提示词...",
  "projects.saveFailedTitle": "保存失败",
  "projects.saveMissingId": "无法保存：缺少模板 ID。",
  "projects.saveFailedMessage": "保存失败：{error}",
  "projects.deleteConnectionError": "无法删除模板，请检查网关连接。",
  "projects.deletedTitle": "模板已删除",
  "projects.deletedMessage": "“{name}”及其关联运行记录已删除。",
  "projects.promptNotLoadedTitle": "提示词未加载",
  "projects.promptNotLoadedMessage": "网关未返回模板提示词。你仍可开始运行，系统将使用默认探索提示词。",
  "projects.nameRequiredTitle": "项目名称必填",
  "projects.nameRequiredMessage": "请输入项目名称。",
  "projects.createFailedTitle": "创建失败",
  "projects.createFailedMessage": "创建项目失败：{error}",
  "projects.runActiveTitle": "已有活动运行",
  "projects.runActiveMessage": "一次仅允许一个活动运行。请先结束或取消当前运行后再启动新运行。",
  "projects.runStartFailedTitle": "无法启动运行",
  "projects.runIdMissingTitle": "缺少运行 ID",
  "projects.runIdMissingMessage": "运行可能已启动，但网关未返回运行 ID。请打开 Runs 并刷新。并检查网关日志中的 POST /v1/projects/executions/run。",

  "runs.tabs.explore": "探索",
  "runs.tabs.activity": "操作",
  "runs.refresh": "刷新",
  "runs.menu": "运行菜单",
  "runs.extend15": "延长 +15 分钟",
  "runs.extend30": "延长 +30 分钟",
  "runs.extend60": "延长 +1 小时",
  "runs.extending": "正在延长…",
  "runs.extendingTitle": "正在延长运行时限",
  "runs.stopping": "正在停止…",
  "runs.stoppingTitle": "正在停止运行",
  "runs.stopDone": "停止 - 完成",
  "runs.stopCancel": "停止 - 取消",
  "runs.deleteRun": "删除运行",
  "runs.markInvalid": "标记无效",
  "runs.invalidAlready": "无效（已标记）",
  "runs.overview": "运行概览",
  "runs.runNumber": "运行编号",
  "runs.loginPhase": "登录阶段",
  "runs.runPhase": "运行阶段",
  "runs.started": "开始时间",
  "runs.timeBudget": "时间预算",
  "runs.duration": "运行时长",
  "runs.target": "目标",
  "runs.mission": "任务",
  "runs.aiAnswerLanguage": "AI 回答语言",
  "runs.aiAnswerLanguageUpdatedTitle": "AI 回答语言已更新",
  "runs.aiAnswerLanguageUpdatedMessage": "已切换为 {language}。",
  "runs.browser": "浏览器",
  "runs.headed": "有界面（可见）",
  "runs.headless": "无界面",
  "runs.recentActivity": "最近操作进展",
  "runs.viewMore": "查看更多 ({count})",
  "runs.noActivityYet": "暂无操作",
  "runs.activityHint":
    "运行中代理应约每 2 分钟提交一次操作记录（若无记录会提醒）：用简短易读的文字说明发现了什么、下一步做什么，并在相关时附上截图。保存后会显示在这里。",
  "runs.confirm.finishTitle": "确定结束本次运行？",
  "runs.confirm.cancelTitle": "确定取消本次运行？",
  "runs.confirm.finishBody": "探索和浏览器会话将停止，运行将被标记为完成。",
  "runs.confirm.cancelBody": "运行将停止且不计为成功完成。适用于本次会话不应视为完成的情况。",
  "runs.confirm.finishNote": "你仍可在右侧继续聊天，补充报告、请求交付数据，并与代理继续处理 PFM 产物。",
  "runs.confirm.error.stop": "无法停止运行：{error}",
  "runs.confirm.notNow": "暂不",
  "runs.confirm.yesFinish": "是，结束运行",
  "runs.confirm.yesCancel": "是，取消运行",
  "runs.confirm.deleteTitle": "删除运行？",
  "runs.confirm.invalidateTitle": "标记运行为无效？",
  "runs.confirm.deleteBody": "这将删除该运行实例及其产物，同时移除此运行产生的模板训练数据。",
  "runs.confirm.invalidateBody": "运行仍可用于审计，但会被标记为“不用于数据报告与训练”，并从模板报告、训练数据和后续继承上下文中移除。",
  "runs.confirm.warning.lastRun": "这是该模板最后一次运行。该模板的训练数据和报告产物将被全部删除。",
  "runs.confirm.warning.onlyValid": "这是该模板唯一有效训练运行。在新的有效运行出现前，模板训练数据将被清空。",
  "runs.confirm.warning.currentSource": "该运行是当前训练来源。模板将改指向最近的其他有效运行。",
  "runs.confirm.warning.keepLatest": "模板将继续使用最近的其他有效运行作为训练数据来源。",
  "runs.confirm.error.actionFailed": "操作失败：{error}",
  "runs.status": "状态",
  "runs.moreActions": "更多运行操作",
  "runs.deleteOrInvalid": "删除或标记无效",
  "runs.invalidBanner": "该运行不用于数据报告与训练。",
  "runs.error.stopRun": "无法停止运行：{error}",
  "runs.pausedBanner": "运行已暂停；请在执行器或网关中恢复后，代理才会继续。恢复前聊天输入将被锁定。",
  "runs.budgetExceeded": "本次运行已超出时间预算。",
  "runs.runningPhaseBanner":
    "运行阶段：代理在真实产品上学习，约每 2 分钟记录一次操作，每 5–10 分钟交付一次。每次交付须包含 .pfm 功能导图与 .FMR 节点表单。节点密度：每层父节点目标 ≤12 个子节点，硬上限 15（后续可能增加整树节点总数上限）。",
  "runs.runningPhaseInitPending":
    "正在进入运行阶段 — 从上一轮有效运行加载继承的 PFM 功能导图与节点报告。",
  "runs.deliveryValidation.rejectedTitle": "PFM 交付被拒绝",
  "runs.deliveryValidation.noChangesTitle": "交付无变化",
  "runs.deliveryValidation.pendingTitle": "交付待导入",
  "runs.deliveryValidation.warningTitle": "交付已保存但有警告",
  "runs.deliveryValidation.acceptedTitle": "PFM 交付已接受",
  "runs.transcriptHint": "会话提示：约 {tokens} tokens",
  "runs.stopNote": "停止备注：{note}",
  "runs.activitySummary": "运行操作 · {entries} 条{screenshots}",
  "runs.entry": "条",
  "runs.entries": "条",
  "runs.screenshot": "张截图",
  "runs.screenshots": "张截图",
  "runs.activityTargetHint": "目标：约每 2 分钟新增一条简洁易读的操作说明，并在相关时附上截图。若无日志，执行器会提醒代理。图片保存在本次运行目录（/v1/.../reports/）。",
  "runs.activity.expand": "点击放大",
  "runs.activity.collapse": "点击收起",
  "runs.activity.viewFullscreen": "全屏查看",
  "runs.activity.fullscreenAria": "全屏操作截图",
  "runs.activity.closeFullscreen": "关闭全屏查看",
  "runs.activity.escapeHint": "按 Escape 返回操作日志。",
  "learning.title": "持续交互式学习",
  "learning.copy": "继承本次运行所学，用于未来的知识学习。",
  "learning.validLabel": "本次运行及其 PFM 与 EAD 报告可作为未来学习的有效来源。",
  "learning.hintActive": "运行仍在进行 — 请在运行结束后再启用。",
  "learning.hintInactive": "若对后续继承学习无价值，可关闭。",
  "learning.optimize": "优化 PFM 功能图",
  "learning.optimizing": "正在优化 PFM 功能图…",
  "learning.optimizeQueued": "已排队优化 — 智能体正在合并节点并更新报告。",
  "learning.optimizeDone": "PFM 功能图已更新（已保存新版本）。",
  "learning.optimizeTimeout": "优化耗时较长。请查看操作日志或稍后重试。",
  "learning.optimizeFailed": "无法启动优化。",
  "learning.createSkills": "创建技能文件",
  "learning.recreateSkills": "重新创建技能文件",
  "learning.confirmInvalidate": "标记为对未来学习无效？",
  "learning.confirmInvalidateCopy":
    "本次运行及其报告将不参与未来继承学习，运行记录仍保留供查阅。",
  "learning.confirmRecreate": "重新创建技能文件？",
  "learning.confirmRecreateCopy":
    "将根据最新已提交的 PFM 树与报告重建技能文件，现有技能文件将被替换。",
  "learning.saving": "保存中…",
  "learning.markInvalid": "标记无效",
  "learning.recreate": "重新创建",
  "learning.cancel": "取消",
  "learning.finishRunToEnable": "请先结束运行，再标记学习有效。",
  "learning.noOwnMapYet": "本次运行尚未生成其 EAD 功能图。",
  "learning.noOwnMapYetHint":
    "请先在本运行完成 PFM 交付（编写 .pfm → import_pfm_delivery）。",

  "runs.pfm.maximize": "最大化功能图与节点报告",
  "runs.pfm.exitMaximize": "退出最大化",
  "runs.pfm.maximizeAria": "最大化功能图与节点报告面板",

  "nodeReport.refresh": "刷新",
  "nodeReport.regenerate": "重新生成",
  "nodeReport.translate": "翻译",
  "nodeReport.translateDisabledEnglish": "当前报告已是英文显示。",
  "nodeReport.translating": "正在翻译报告（仅用于显示）...",
  "nodeReport.translatedViewNote": "当前显示为译文（{language}），仅用于查看，不会保存。",
  "nodeReport.generateFull": "生成完整报告",
  "nodeReport.refreshPfm": "刷新 PFM 节点报告",
  "nodeReport.generatePfm": "生成 PFM 节点报告",
  "nodeReport.pleaseWait": "请稍候",
  "nodeReport.refreshMenu": "刷新菜单",
  "nodeReport.longFormatRequired": "当前节点报告不符合必需的长格式（功能、测试用例、操作步骤），请使用“重新生成”。",
  "nodeReport.incompleteExplorationNote":
    "此节点目前仅有标题和描述。需要进一步探索才能完成完整的 EAD 报告（功能、测试用例与测试步骤）。探索后请点击刷新。",
  "nodeReport.incompleteExplorationSaved":
    "已保存“需要更多探索”说明。请继续探索该节点，然后刷新以补充功能、测试用例与步骤。",
};

const JA: Dict = {
  ...EN,
  "brand.name": "EAD Explore",
  "lang.select": "言語を選択",
  "lang.current": "現在の言語",
  "lang.zh": "中国語",
  "lang.ja": "日本語",
  "lang.ko": "韓国語",
  "tab.eadDiagramStandard": "EAD ダイアグラム標準",
  "tab.eadSkills": "EAD スキル",
  "tab.eadWorkflowRules": "EAD ワークフロールール",
  "tab.exploreTypes": "探索タイプ",
  "tab.projects": "プロジェクトテンプレート",
  "tab.runs": "自動テスト実行",
  "tab.closedRuns": "完了した実行",
  "tab.generateChat": "プロジェクトチャット",
  "nav.explorePlanning": "探索計画",
  "nav.activeProjectRun": "進行中の実行",
  "nav.projectInteractive": "プロジェクトインタラクティブ",
  "nav.learningExplore": "学習用 EAD Explore",
  "nav.completedProjectRun": "完了したプロジェクト実行",
  "nav.searchRuns": "実行を検索",
  "nav.searchTemplateRun": "テンプレート/実行を検索",
  "nav.noRunsYet": "実行はまだありません",
  "nav.gateway": "ゲートウェイ",
  "nav.online": "オンライン",
  "nav.offline": "オフライン",
  "projects.loading": "テンプレートを読み込み中...",
  "projects.templates": "テンプレート ({count})",
  "projects.new": "+ 新規",
  "projects.refreshing": "一覧を更新中...",
  "projects.selectTemplate": "一覧からテンプレートを選択してください",
  "projects.createTitle": "新しい探索プロジェクト",
  "projects.createSubtitle": "実行前にプロジェクト定義を作成します。",
  "projects.cancel": "キャンセル",
  "projects.create": "作成",
  "projects.creating": "作成中...",
  "projects.chooseLearningModeTitle": "学習モードを選択",
  "projects.chooseLearningModeSubtitle": "この実行の主目的を選びます。AI の動作と配信の焦点が変わります。",
  "projects.learningModePfmTitle": "製品機能マップ探索",
  "projects.learningModePfmDesc": "PFM ノードツリー（構造・階層・品質）を構築・改善します。",
  "projects.learningModeDeepTitle": "ディープラーニング（機能とテストケース）",
  "projects.learningModeDeepDesc": "ツリーを大きく変えずにノードレポートを深掘りします。",
  "projects.learningModeContinue": "続ける",
  "projects.learningModeBack": "戻る",
  "projects.learningModeRequiredFirst": "このテンプレートに基準 PFM ツリーがありません。先に機能マップ探索を完了してください。",
  "projects.startRunTitle": "探索実行を開始",
  "projects.startRunSubtitle": "実行前に設定を確認してください。",
  "projects.startRun": "実行開始",
  "projects.starting": "開始中...",
  "projects.editTitle": "探索プロジェクトを編集",
  "projects.editSubtitle": "プロジェクト定義を更新して保存します。",
  "projects.save": "保存",
  "projects.saving": "保存中...",
  "projects.deleteConfirm": "探索プロジェクトを削除しますか？",
  "projects.deleteTemplate": "テンプレートを削除",
  "projects.notNow": "今はしない",
  "projects.field.name": "プロジェクト名",
  "projects.field.targetUrl": "対象 URL",
  "projects.field.description": "説明",
  "projects.field.aiAnswerLanguage": "AI 回答言語",
  "projects.answerLanguage.zh-tw": "繁體中文",
  "projects.field.aiPrompt": "AI プロンプト",
  "runs.tabs.explore": "探索",
  "runs.tabs.activity": "アクティビティ",
  "runs.refresh": "更新",
  "runs.menu": "実行メニュー",
  "runs.extend15": "+15分延長",
  "runs.extend30": "+30分延長",
  "runs.extend60": "+1時間延長",
  "runs.extending": "延長中…",
  "runs.extendingTitle": "実行時間を延長しています",
  "runs.stopping": "停止中…",
  "runs.stoppingTitle": "実行を停止しています",
  "runs.aiAnswerLanguage": "AI 回答言語",
  "runs.aiAnswerLanguageUpdatedTitle": "AI 回答言語を更新しました",
  "runs.aiAnswerLanguageUpdatedMessage": "AI の回答言語を {language} に切り替えました。",
  "runs.stopDone": "停止 - 完了",
  "runs.stopCancel": "停止 - キャンセル",
  "runs.deleteRun": "実行を削除",
  "runs.markInvalid": "無効にする",
  "runs.invalidAlready": "無効（設定済み）",
  "runs.overview": "実行概要",
  "runs.runNumber": "実行番号",
  "runs.loginPhase": "ログインフェーズ",
  "runs.runPhase": "実行フェーズ",
  "runs.recentActivity": "最近の進捗ログ",
  "runs.noActivityYet": "まだアクティビティはありません",
  "nodeReport.refresh": "更新",
  "nodeReport.regenerate": "再生成",
  "nodeReport.translate": "翻訳",
  "nodeReport.translateDisabledEnglish": "レポートは既に英語表示です。",
  "nodeReport.translating": "表示用にレポートを翻訳しています...",
  "nodeReport.translatedViewNote": "翻訳表示中（{language}）。これは表示専用で保存されません。",
  "nodeReport.generateFull": "完全レポートを生成",
  "nodeReport.refreshPfm": "PFM ノードレポートを更新",
  "nodeReport.generatePfm": "PFM ノードレポートを生成",
  "nodeReport.pleaseWait": "お待ちください",
  "nodeReport.refreshMenu": "更新メニュー",
  "nodeReport.longFormatRequired": "このノードレポートは必須の長形式（機能、テストケース、手順）ではありません。再生成してください。",
  "nodeReport.incompleteExplorationNote":
    "このノードはタイトルと説明のみです。完全な EAD レポート（機能・テストケース・手順）には追加の探索が必要です。探索後に更新してください。",
  "nodeReport.incompleteExplorationSaved":
    "探索が必要である旨を保存しました。引き続き探索し、機能・テストケース・手順を追加するには更新してください。",
};

const KO: Dict = {
  ...EN,
  "brand.name": "EAD Explore",
  "lang.select": "언어 선택",
  "lang.current": "현재 언어",
  "lang.zh": "중국어",
  "lang.ja": "일본어",
  "lang.ko": "한국어",
  "tab.eadDiagramStandard": "EAD 다이어그램 표준",
  "tab.eadSkills": "EAD 스킬",
  "tab.eadWorkflowRules": "EAD 워크플로우 규칙",
  "tab.exploreTypes": "탐색 유형",
  "tab.projects": "프로젝트 템플릿",
  "tab.runs": "자동 테스트 실행",
  "tab.closedRuns": "완료된 실행",
  "tab.generateChat": "프로젝트 채팅",
  "nav.explorePlanning": "탐색 계획",
  "nav.activeProjectRun": "진행 중 실행",
  "nav.projectInteractive": "프로젝트 인터랙티브",
  "nav.learningExplore": "학습용 EAD Explore",
  "nav.completedProjectRun": "완료된 프로젝트 실행",
  "nav.searchRuns": "실행 검색",
  "nav.searchTemplateRun": "템플릿/실행 검색",
  "nav.noRunsYet": "실행이 없습니다",
  "nav.gateway": "게이트웨이",
  "nav.online": "온라인",
  "nav.offline": "오프라인",
  "projects.loading": "템플릿 불러오는 중...",
  "projects.templates": "템플릿 ({count})",
  "projects.new": "+ 새로 만들기",
  "projects.refreshing": "목록 새로고침 중...",
  "projects.selectTemplate": "목록에서 템플릿을 선택하세요",
  "projects.createTitle": "새 탐색 프로젝트",
  "projects.createSubtitle": "실행 시작 전에 프로젝트 정의를 만듭니다.",
  "projects.cancel": "취소",
  "projects.create": "생성",
  "projects.creating": "생성 중...",
  "projects.chooseLearningModeTitle": "학습 모드 선택",
  "projects.chooseLearningModeSubtitle": "이번 실행의 주요 목표를 선택합니다. AI 동작과 전달 초점이 달라집니다.",
  "projects.learningModePfmTitle": "제품 기능 맵 탐색",
  "projects.learningModePfmDesc": "PFM 노드 트리(구조, 계층, 품질)를 구축하거나 개선합니다.",
  "projects.learningModeDeepTitle": "딥 러닝(기능 및 테스트 케이스)",
  "projects.learningModeDeepDesc": "트리는 유지한 채 노드 리포트를 심화합니다.",
  "projects.learningModeContinue": "계속",
  "projects.learningModeBack": "뒤로",
  "projects.learningModeRequiredFirst": "이 템플릿에 기준 PFM 트리가 없습니다. 먼저 기능 맵 탐색 실행을 완료하세요.",
  "projects.startRunTitle": "탐색 실행 시작",
  "projects.startRunSubtitle": "실행 시작 전에 설정을 확인하세요.",
  "projects.startRun": "실행 시작",
  "projects.starting": "시작 중...",
  "projects.editTitle": "탐색 프로젝트 편집",
  "projects.editSubtitle": "프로젝트 정의를 수정하고 저장합니다.",
  "projects.save": "저장",
  "projects.saving": "저장 중...",
  "projects.deleteConfirm": "탐색 프로젝트를 삭제할까요?",
  "projects.deleteTemplate": "템플릿 삭제",
  "projects.notNow": "나중에",
  "projects.field.name": "프로젝트 이름",
  "projects.field.targetUrl": "대상 URL",
  "projects.field.description": "설명",
  "projects.field.aiAnswerLanguage": "AI 답변 언어",
  "projects.answerLanguage.zh-tw": "중국어(번체)",
  "projects.field.aiPrompt": "AI 프롬프트",
  "runs.tabs.explore": "탐색",
  "runs.tabs.activity": "활동",
  "runs.refresh": "새로고침",
  "runs.menu": "실행 메뉴",
  "runs.extend15": "+15분 연장",
  "runs.extend30": "+30분 연장",
  "runs.extend60": "+1시간 연장",
  "runs.extending": "연장 중…",
  "runs.extendingTitle": "실행 시간 예산 연장 중",
  "runs.stopping": "중지 중…",
  "runs.stoppingTitle": "실행 중지 중",
  "runs.aiAnswerLanguage": "AI 답변 언어",
  "runs.aiAnswerLanguageUpdatedTitle": "AI 답변 언어가 업데이트되었습니다",
  "runs.aiAnswerLanguageUpdatedMessage": "에이전트 답변 언어가 {language}(으)로 변경되었습니다.",
  "runs.stopDone": "중지 - 완료",
  "runs.stopCancel": "중지 - 취소",
  "runs.deleteRun": "실행 삭제",
  "runs.markInvalid": "무효로 표시",
  "runs.invalidAlready": "무효(이미 표시됨)",
  "runs.overview": "실행 개요",
  "runs.runNumber": "실행 번호",
  "runs.loginPhase": "로그인 단계",
  "runs.runPhase": "실행 단계",
  "runs.recentActivity": "최근 활동 진행",
  "runs.noActivityYet": "아직 활동이 없습니다",
  "nodeReport.refresh": "새로고침",
  "nodeReport.regenerate": "재생성",
  "nodeReport.translate": "번역",
  "nodeReport.translateDisabledEnglish": "이미 영어로 표시되고 있습니다.",
  "nodeReport.translating": "표시용으로 리포트를 번역하는 중...",
  "nodeReport.translatedViewNote": "번역된 보기({language})를 표시 중입니다. 저장되지 않습니다.",
  "nodeReport.generateFull": "전체 리포트 생성",
  "nodeReport.refreshPfm": "PFM 노드 리포트 새로고침",
  "nodeReport.generatePfm": "PFM 노드 리포트 생성",
  "nodeReport.pleaseWait": "잠시만 기다려 주세요",
  "nodeReport.refreshMenu": "새로고침 메뉴",
  "nodeReport.longFormatRequired": "이 노드 리포트는 필수 장문 형식(기능, 테스트 케이스, 절차 단계)이 아닙니다. 재생성해 주세요.",
  "nodeReport.incompleteExplorationNote":
    "이 노드는 제목과 설명만 있습니다. 완전한 EAD 리포트(기능, 테스트 케이스, 절차 단계)를 위해 추가 탐색이 필요합니다. 탐색 후 새로고침하세요.",
  "nodeReport.incompleteExplorationSaved":
    "추가 탐색이 필요함을 저장했습니다. 계속 탐색한 뒤 기능·테스트 케이스·단계를 추가하려면 새로고침하세요.",
};

const DICT: Record<UiLocale, Dict> = {
  en: EN,
  zh: ZH,
  ja: JA,
  ko: KO,
};

function normalizeLocale(raw: unknown): UiLocale {
  const v = String(raw || "").trim().toLowerCase();
  return LOCALES.includes(v as UiLocale) ? (v as UiLocale) : "en";
}

export function getLocale(): UiLocale {
  try {
    return normalizeLocale(localStorage.getItem(LOCALE_KEY));
  } catch {
    return "en";
  }
}

export function setLocale(locale: UiLocale): UiLocale {
  const next = normalizeLocale(locale);
  try {
    localStorage.setItem(LOCALE_KEY, next);
  } catch {
    /* ignore */
  }
  if (typeof document !== "undefined") {
    document.documentElement.setAttribute("lang", next);
  }
  localeBus.dispatchEvent(new CustomEvent("locale-change", { detail: next }));
  return next;
}

export function subscribeLocale(listener: (locale: UiLocale) => void): () => void {
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<UiLocale>).detail;
    listener(normalizeLocale(detail));
  };
  localeBus.addEventListener("locale-change", handler);
  return () => localeBus.removeEventListener("locale-change", handler);
}

export function t(key: string, params?: Record<string, string | number>, locale?: UiLocale): string {
  const l = normalizeLocale(locale || getLocale());
  const tpl = DICT[l][key] ?? DICT.en[key] ?? key;
  if (!params) return tpl;
  return tpl.replace(/\{(\w+)\}/g, (_m, k) => String(params[k] ?? ""));
}

if (typeof document !== "undefined") {
  document.documentElement.setAttribute("lang", getLocale());
}
